import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import ProspectaSimulacaoModal from "./ProspectaSimulacaoModal";
import ProspectaTimelineModal from "./ProspectaTimelineModal";
import DocorbanConsultaModal from "../components/DocorbanConsultaModal";
import {
  Briefcase, Search, RefreshCw, MessageCircle, Trash2, Copy, Save,
  ShieldCheck, Users, CheckCircle2, Clock, XCircle, PhoneOff, Sparkles, Webhook, FileSpreadsheet, Mail,
  Landmark
} from "lucide-react";

const STATUS_META = {
  NOVO: { label: "Novo", cor: "bg-blue-100 text-blue-700", icon: Sparkles },
  EM_ATENDIMENTO: { label: "Em atendimento", cor: "bg-amber-100 text-amber-700", icon: Clock },
  QUALIFICADO: { label: "Qualificado", cor: "bg-violet-100 text-violet-700", icon: CheckCircle2 },
  CONVERTIDO: { label: "Convertido", cor: "bg-green-100 text-green-700", icon: CheckCircle2 },
  REJEITADO: { label: "Rejeitado", cor: "bg-red-100 text-red-700", icon: XCircle },
  SEM_CONTATO: { label: "Sem contato", cor: "bg-gray-100 text-gray-600", icon: PhoneOff },
};

const brl = (cents) => {
  const v = (Number(cents) || 0) / 100;
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const TAG_META = {
  "Captação": "bg-blue-100 text-blue-700",
  "Em Simulação": "bg-violet-100 text-violet-700",
  "Com Valores": "bg-emerald-100 text-emerald-700",
  "Sem Valores": "bg-amber-100 text-amber-700",
  "Sem Interesse": "bg-gray-100 text-gray-600",
  "Margem Negativa": "bg-red-100 text-red-700",
  "Depósito": "bg-teal-100 text-teal-700",
  "Aceitou a Oferta": "bg-green-600 text-white",
};
const TAG_OPTS = Object.keys(TAG_META);

export default function ProspectaCLT() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState({ total: 0, por_status: {}, pre_aprovados: 0 });
  const [statusFiltro, setStatusFiltro] = useState("TODOS");
  const [search, setSearch] = useState("");
  const [soEmailMkt, setSoEmailMkt] = useState(false);
  const [tagFiltro, setTagFiltro] = useState("TODAS");
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [simLead, setSimLead] = useState(null);
  const [timelineLead, setTimelineLead] = useState(null);
  const [docConsulta, setDocConsulta] = useState(null);
  const [filtroDados, setFiltroDados] = useState("todos");
  const [bulk, setBulk] = useState(null);
  const [ofertaFiltro, setOfertaFiltro] = useState("todos");
  const [ofertaStats, setOfertaStats] = useState({ com_oferta: 0, valor_total: 0, sem_oferta: 0 });
  const [higien, setHigien] = useState(null);
  const fmtNasc = (d) => {
    if (!d) return "—";
    const s = String(d).slice(0, 10);
    const [y, m, dd] = s.split("-");
    return (y && m && dd) ? `${dd}/${m}/${y}` : s;
  };
  const fmtDT = (d) => (d ? new Date(d).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : null);
  const limit = 50;

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (statusFiltro !== "TODOS") params.append("status", statusFiltro);
      if (soEmailMkt) params.append("origem", "EMAIL_MKT");
      if (tagFiltro !== "TODAS") params.append("tag", tagFiltro);
      if (filtroDados !== "todos") params.append("completude", filtroDados);
      if (ofertaFiltro !== "todos") params.append("oferta", ofertaFiltro);
      if (search.trim()) params.append("search", search.trim());
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/prospecta/leads?${params.toString()}`),
        axios.get(`${API}/prospecta/leads/stats`),
        axios.get(`${API}/prospecta/leads/oferta-stats`),
      ]);
      setLeads(r1.data.items || []);
      setTotal(r1.data.total || 0);
      setStats(r2.data || {});
      setOfertaStats(r3.data || {});
    } catch (e) {
      toast.error("Erro ao carregar leads Prospecta");
    } finally {
      setLoading(false);
    }
  }, [page, statusFiltro, search, limit, soEmailMkt, tagFiltro, filtroDados, ofertaFiltro]);

  useEffect(() => { carregar(); }, [carregar]);

  const carregarConfig = async () => {
    try { const r = await axios.get(`${API}/prospecta/config`); setConfig(r.data); }
    catch { toast.error("Erro ao carregar configuração"); }
  };
  useEffect(() => { carregarConfig(); }, []);

  // Polling do disparo em massa
  useEffect(() => {
    if (!bulk?.running) return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/prospecta/clt/notificar-todos/status`);
        setBulk(r.data);
        if (!r.data.running) {
          clearInterval(t);
          toast.success(`Disparo concluído: ${r.data.enviados} enviados de ${r.data.total}`);
          carregar();
        }
      } catch { /* ignore */ }
    }, 2500);
    return () => clearInterval(t);
  }, [bulk?.running, carregar]);

  // Polling da higienização em massa
  useEffect(() => {
    if (!higien?.running) return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/prospecta/clt/higienizar-todos/status`);
        setHigien(r.data);
        if (!r.data.running) {
          clearInterval(t);
          toast.success(`Higienização concluída: ${r.data.simulados || 0} simulados · ${r.data.com_oferta || 0} com oferta`);
          carregar();
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(t);
  }, [higien?.running, carregar]);

  useEffect(() => {
    axios.get(`${API}/prospecta/clt/higienizar-todos/status`).then((r) => {
      if (r.data?.running) setHigien(r.data);
    }).catch(() => {});
  }, []);

  // Carrega status inicial do disparo (caso já esteja rodando)
  useEffect(() => {
    axios.get(`${API}/prospecta/clt/notificar-todos/status`).then((r) => {
      if (r.data?.running) setBulk(r.data);
    }).catch(() => {});
  }, []);

  const mudarStatus = async (lead, novoStatus) => {
    try {
      await axios.patch(`${API}/prospecta/leads/${lead.id}`, { status: novoStatus });
      toast.success("Status atualizado");
      carregar();
    } catch { toast.error("Erro ao atualizar status"); }
  };

  const mudarTag = async (lead, tag) => {
    try {
      await axios.patch(`${API}/prospecta/leads/${lead.id}`, { tag_cliente: tag });
      toast.success("Tag atualizada");
      setLeads((prev) => prev.map((x) => (x.id === lead.id ? { ...x, tag_cliente: tag } : x)));
    } catch { toast.error("Erro ao atualizar tag"); }
  };

  const notificarWpp = async (lead) => {
    try {
      await axios.post(`${API}/prospecta/leads/${lead.id}/notificar-wpp`);
      toast.success("WhatsApp enviado!");
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar WhatsApp");
    }
  };

  const excluir = async (lead) => {
    if (!window.confirm("Excluir este lead?")) return;
    try { await axios.delete(`${API}/prospecta/leads/${lead.id}`); toast.success("Lead excluído"); carregar(); }
    catch { toast.error("Erro ao excluir"); }
  };

  const salvarConfig = async () => {
    try {
      await axios.put(`${API}/prospecta/config`, {
        webhook_secret: config.webhook_secret || "",
        wpp_notificar: !!config.wpp_notificar,
        wpp_session_id: config.wpp_session_id || "pabx_clt",
        wpp_template: config.wpp_template || "",
        api_email: config.api_email || "",
        api_senha: config.api_senha || "",
        clt_auto_simular: !!config.clt_auto_simular,
        clt_auto_notificar: !!config.clt_auto_notificar,
        clt_test_mode: !!config.clt_test_mode,
        clt_test_phone: config.clt_test_phone || "",
        clt_notif_template: config.clt_notif_template || "",
        clt_auto_campanha: !!config.clt_auto_campanha,
        clt_campanha_voip_id: config.clt_campanha_voip_id || "",
      });
      toast.success("Configuração salva!");
    } catch { toast.error("Erro ao salvar configuração"); }
  };

  const dispararTodos = async () => {
    if (!window.confirm(
      "Direcionar TODOS os leads CLT para a campanha VoIP \"Prospecta CLT\"?\n\n" +
      "Fluxo: consulta Nova Vida (quem não tem) → adiciona o contato na campanha → inicia a discagem (ligação → tecla 1 → SMS + WhatsApp).\n\n" +
      "⚠️ Isto irá LIGAR para os clientes reais. Deseja continuar?"
    )) return;
    try {
      await axios.post(`${API}/prospecta/clt/notificar-todos`, { nova_vida: true, delay_seg: 2 });
      toast.success("Direcionamento iniciado!");
      setBulk({ running: true, processados: 0, total: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao iniciar disparo");
    }
  };

  const higienizarTodos = async () => {
    if (!window.confirm(
      "Higienizar TODA a base na Nova Vida (clientes sem nome da mãe) e recalcular o valor via simulação Prospecta CLT (Paypro)?\n\n" +
      "⚠️ Isto CONSOME crédito da Nova Vida e da Prospecta por cliente e pode levar horas. Já pula quem foi simulado nos últimos 7 dias. Continuar?"
    )) return;
    try {
      await axios.post(`${API}/prospecta/clt/higienizar-todos`, { confirmar: true, pular_recentes_dias: 7, concorrencia: 4 });
      toast.success("Higienização iniciada em segundo plano!");
      setHigien({ running: true, processados: 0, total: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao iniciar higienização");
    }
  };

  const cancelarHigien = async () => {
    try { await axios.post(`${API}/prospecta/clt/higienizar-todos/cancelar`); toast.info("Cancelando higienização..."); }
    catch { toast.error("Falha ao cancelar"); }
  };

  const copiar = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiado!"); };

  const [exportando, setExportando] = useState(null);
  const exportarMkt = async (somenteNovos) => {
    setExportando(somenteNovos ? "novos" : "todos");
    try {
      const r = await axios.get(
        `${API}/prospecta/leads/export-mkt?somente_novos=${somenteNovos}`,
        { responseType: "blob" }
      );
      const qtd = Number(r.headers["x-exportados"] || 0);
      if (!qtd) {
        toast.info(somenteNovos
          ? "Nenhum lead novo com e-mail para exportar."
          : "Nenhum lead com e-mail encontrado.");
        return;
      }
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads_mkt_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${qtd} lead(s) exportado(s) para marketing!`);
      carregar();
    } catch (e) {
      toast.error("Falha ao gerar arquivo Excel");
    } finally {
      setExportando(null);
    }
  };

  const exportarMktComOferta = async () => {
    setExportando("oferta");
    try {
      const r = await axios.get(
        `${API}/prospecta/leads/export-mkt-com-oferta`,
        { responseType: "blob" }
      );
      const qtd = Number(r.headers["x-exportados"] || 0);
      if (!qtd) { toast.info("Nenhum lead com oferta encontrado."); return; }
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `clt_com_oferta_mkt_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${qtd} linha(s) exportada(s) (com oferta: nome/banco/valor/prazo/parcela + e-mails)!`);
    } catch (e) {
      toast.error("Falha ao gerar arquivo com oferta");
    } finally {
      setExportando(null);
    }
  };

  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="p-6 space-y-6" data-testid="prospecta-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-violet-600" /> Prospecta CLT
          </h1>
          <p className="text-sm text-muted-foreground">Leads de crédito consignado CLT recebidos via integração Prospecta</p>
        </div>
        <Button variant="outline" onClick={carregar} className="gap-2" data-testid="prospecta-refresh">
          <RefreshCw className="h-4 w-4" /> Atualizar
        </Button>
      </div>

      <Tabs defaultValue="leads">
        <TabsList>
          <TabsTrigger value="leads" data-testid="tab-leads"><Users className="h-4 w-4 mr-1" /> Leads</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config"><Webhook className="h-4 w-4 mr-1" /> Configuração</TabsTrigger>
        </TabsList>

        {/* ===== LEADS ===== */}
        <TabsContent value="leads" className="space-y-5">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <Card data-testid="stat-total"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-violet-600">{stats.total || 0}</p>
              <p className="text-[11px] text-gray-500 uppercase">Total</p>
            </CardContent></Card>
            <Card data-testid="stat-preaprovados"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{stats.pre_aprovados || 0}</p>
              <p className="text-[11px] text-gray-500 uppercase">Pré-aprovados</p>
            </CardContent></Card>
            {Object.keys(STATUS_META).map((st) => (
              <Card key={st} data-testid={`stat-${st}`}><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold">{stats.por_status?.[st] || 0}</p>
                <p className="text-[11px] text-gray-500 uppercase leading-tight">{STATUS_META[st].label}</p>
              </CardContent></Card>
            ))}
          </div>

          {/* Filtros */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input className="pl-9" placeholder="Buscar por nome, CPF ou telefone..." value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="prospecta-search" />
            </div>
            <Select value={statusFiltro} onValueChange={(v) => { setStatusFiltro(v); setPage(1); }}>
              <SelectTrigger className="w-[200px]" data-testid="prospecta-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos os status</SelectItem>
                {Object.keys(STATUS_META).map((st) => <SelectItem key={st} value={st}>{STATUS_META[st].label}</SelectItem>)}
              </SelectContent>
            </Select>
            <button type="button" onClick={() => { setSoEmailMkt((v) => !v); setPage(1); }} data-testid="filtro-email-mkt"
              className={`flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${soEmailMkt ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-cyan-700 border-cyan-200 hover:bg-cyan-50"}`}>
              <Mail className="h-4 w-4" /> {soEmailMkt ? "Mostrando: E-mail MKT" : "Só E-mail MKT"}
            </button>
            <Select value={tagFiltro} onValueChange={(v) => { setTagFiltro(v); setPage(1); }}>
              <SelectTrigger className="w-[190px]" data-testid="prospecta-tag-filter"><SelectValue placeholder="Todas as tags" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TODAS">Todas as tags</SelectItem>
                {TAG_OPTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="text-center py-10 text-gray-400">Carregando...</div>
              ) : leads.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Briefcase className="h-10 w-10 mx-auto mb-2 opacity-20" />
                  <p className="font-medium">Nenhum lead recebido ainda</p>
                  <p className="text-sm">Os leads enviados pela Prospecta aparecerão aqui automaticamente.</p>
                </div>
              ) : (
                <>
                <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 flex items-center gap-x-6 gap-y-1 flex-wrap" data-testid="oferta-summary">
                  <span className="text-sm text-emerald-800">💰 <b data-testid="resumo-com-oferta">{ofertaStats.com_oferta || 0}</b> clientes com oferta</span>
                  <span className="text-sm text-emerald-800">Oportunidade total: <b className="text-emerald-700" data-testid="resumo-valor-total">{brl((ofertaStats.valor_total || 0) * 100)}</b></span>
                  <span className="text-sm text-red-600">❌ <b data-testid="resumo-sem-oferta">{ofertaStats.sem_oferta || 0}</b> sem oferta</span>
                </div>
                {higien && (higien.running || higien.processados > 0) && (
                  <div className="mb-3 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2" data-testid="higien-progress">
                    <div className="flex items-center justify-between text-xs text-violet-800 mb-1">
                      <span className="font-medium">{higien.running ? "Higienizando base (Nova Vida + Simular)..." : "Higienização concluída ✅"}</span>
                      <span className="flex items-center gap-2">
                        {higien.processados || 0} / {higien.total || 0}
                        {higien.running && <button type="button" onClick={cancelarHigien} className="text-red-600 underline" data-testid="btn-cancelar-higien">cancelar</button>}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-violet-100 overflow-hidden">
                      <div className="h-full bg-violet-500 transition-all" style={{ width: `${higien.total ? Math.round((higien.processados / higien.total) * 100) : 0}%` }} />
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-[11px] text-slate-600">
                      <span>🔎 Nova Vida: <b>{higien.enriquecidos || 0}</b></span>
                      <span>🧮 Simulados: <b>{higien.simulados || 0}</b></span>
                      <span>✅ Com oferta: <b className="text-emerald-700">{higien.com_oferta || 0}</b></span>
                      <span>❌ Sem oferta: <b>{higien.sem_oferta || 0}</b></span>
                      <span>⏭️ Pulados: <b>{higien.pulados || 0}</b></span>
                      <span>⚠️ Erros: <b>{higien.erros || 0}</b></span>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap" data-testid="filtro-dados-clt">
                  <div className="flex items-center gap-2 flex-wrap">
                  {[["todos", "Todos"], ["completos", "Dados Mínimo Completa"], ["faltando", "Faltam dados"]].map(([k, lab]) => {
                    const n = k === "todos" ? (stats.total || 0) : (k === "completos" ? (stats.sim_completos || 0) : (stats.sim_faltam || 0));
                    const ativo = filtroDados === k && ofertaFiltro === "todos";
                    return (
                      <button key={k} type="button" onClick={() => { setFiltroDados(k); setOfertaFiltro("todos"); setPage(1); }} data-testid={`filtro-${k}`}
                        className={`text-xs px-3 py-1 rounded-full border transition-colors ${ativo ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                        {lab} ({n})
                      </button>
                    );
                  })}
                  <button type="button" onClick={() => { setOfertaFiltro("com"); setFiltroDados("todos"); setPage(1); }} data-testid="filtro-com-oferta"
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${ofertaFiltro === "com" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50"}`}>
                    ✅ Com oferta ({ofertaStats.com_oferta || 0})
                  </button>
                  <button type="button" onClick={() => { setOfertaFiltro("sem"); setFiltroDados("todos"); setPage(1); }} data-testid="filtro-sem-oferta"
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${ofertaFiltro === "sem" ? "bg-red-500 text-white border-red-500" : "bg-white text-red-600 border-red-200 hover:bg-red-50"}`}>
                    ❌ Sem oferta ({ofertaStats.sem_oferta || 0})
                  </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs text-violet-700 border-violet-200 hover:bg-violet-50"
                      onClick={higienizarTodos} disabled={higien?.running} data-testid="btn-higienizar-base">
                      <Sparkles className="h-3.5 w-3.5" /> {higien?.running ? "Higienizando..." : "Higienizar base (Nova Vida + Simular)"}
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => exportarMkt(true)} disabled={!!exportando} data-testid="btn-export-mkt-novos">
                      <Mail className="h-3.5 w-3.5" /> {exportando === "novos" ? "Gerando..." : "Gerar Novos (MKT)"}
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs text-slate-700 border-slate-200 hover:bg-slate-50"
                      onClick={() => exportarMkt(false)} disabled={!!exportando} data-testid="btn-export-mkt-todos">
                      <FileSpreadsheet className="h-3.5 w-3.5" /> {exportando === "todos" ? "Gerando..." : "Gerar Todos (MKT)"}
                    </Button>
                    <Button size="sm" className="h-8 gap-1.5 text-xs bg-green-600 hover:bg-green-700 text-white"
                      onClick={exportarMktComOferta} disabled={!!exportando} data-testid="btn-export-mkt-com-oferta">
                      <FileSpreadsheet className="h-3.5 w-3.5" /> {exportando === "oferta" ? "Gerando..." : "Gerar Com Oferta (SMS+Email)"}
                    </Button>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Nascimento</TableHead>
                      <TableHead>Nome da mãe</TableHead>
                      <TableHead>CPF</TableHead>
                      <TableHead>Telefone</TableHead>
                      <TableHead>E-mail</TableHead>
                      <TableHead>Margem</TableHead>
                      <TableHead>Valor Disponível</TableHead>
                      <TableHead>Empregador</TableHead>
                      <TableHead>Tag Cliente</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Consulta Nova Vida</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((l) => {
                      const cp = l.creditProfile || {};
                      const meta = STATUS_META[l.status] || STATUS_META.NOVO;
                      return (
                        <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                          <TableCell>
                            <div className="font-medium">{l.nome || "—"}</div>
                            <div className="flex items-center gap-1 mt-0.5">
                              {l.preApproved && <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0">Pré-aprovado</Badge>}
                              {l.pep && <Badge className="bg-orange-100 text-orange-700 text-[10px] px-1.5 py-0">PEP</Badge>}
                              {l.wpp_notificado && <Badge className="bg-green-50 text-green-700 text-[10px] px-1.5 py-0">WhatsApp ✓</Badge>}
                              {l.mkt_exportado && <Badge className="bg-indigo-50 text-indigo-700 text-[10px] px-1.5 py-0" title="Enviado para e-mail marketing">MKT ✓</Badge>}
                              {l.landing_mkt && <Badge className="bg-cyan-100 text-cyan-700 text-[10px] px-1.5 py-0" title="Cliente veio da landing de E-mail Marketing">E-mail MKT</Badge>}
                              {l.landing_mkt && l.landing_etapa_label && <Badge className="bg-slate-100 text-slate-600 text-[10px] px-1.5 py-0" title="Até onde o cliente avançou no fluxo">➜ {l.landing_etapa_label}</Badge>}
                              {!l.dados_completos && <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">Faltam dados</Badge>}
                              {l.precisa_migrar && <Badge className="bg-rose-100 text-rose-700 text-[10px] px-1.5 py-0" title="Tem contatos que ainda não foram migrados para as campanhas VoIP/SMS">Migrar VoIP/SMS</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{fmtNasc(l.data_nascimento)}</TableCell>
                          <TableCell className="text-xs">{l.nome_mae || "—"}</TableCell>
                          <TableCell className="font-mono text-xs">{l.cpf || "—"}</TableCell>
                          <TableCell className="text-sm">
                            {(l.telefones_todos && l.telefones_todos.length) ? (
                              <div className="flex flex-col" title={l.telefones_todos.join(", ")} data-testid={`tels-${l.id}`}>
                                <span>{l.telefones_todos[0]}</span>
                                {l.telefones_todos.length > 1 && <span className="text-[10px] text-cyan-600 font-semibold">+{l.telefones_todos.length - 1} telefone(s)</span>}
                              </div>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="text-xs">
                            {(l.emails_todos && l.emails_todos.length) ? (
                              <div className="flex flex-col" title={l.emails_todos.join(", ")} data-testid={`emails-${l.id}`}>
                                <span>{l.emails_todos[0]}</span>
                                {l.emails_todos.length > 1 && <span className="text-[10px] text-cyan-600 font-semibold">+{l.emails_todos.length - 1} e-mail(s)</span>}
                              </div>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="text-sm font-semibold text-emerald-700" data-testid={`margem-${l.id}`}>{l.margem ? brl(l.margem * 100) : <span className="text-slate-400 font-normal">—</span>}</TableCell>
                          <TableCell className="text-sm">
                            {l.valor_disponivel ? (
                              <div className="flex flex-col" data-testid={`vd-${l.id}`}
                                title={(l.valores_disponiveis?.opcoes || []).map((o) => `${o.prazo}x: ${brl(o.valor_disponivel * 100)} (parcela ${brl(o.parcela * 100)})`).join("\n")}>
                                <span className="font-bold text-violet-700">{brl(l.valor_disponivel * 100)}</span>
                                <span className="text-[10px] text-slate-500">{l.valores_disponiveis?.banco ? l.valores_disponiveis.banco + " · " : ""}até {l.valores_disponiveis?.prazo_max}x · {(l.valores_disponiveis?.opcoes || []).length} opções</span>
                              </div>
                            ) : l.sim_status === "SEM_OFERTA" ? (
                              <span className="text-red-500 text-xs font-semibold" data-testid={`vd-sem-oferta-${l.id}`}>Sem oferta</span>
                            ) : <span className="text-slate-400 text-xs" data-testid={`vd-nao-simulado-${l.id}`}>—</span>}
                          </TableCell>
                          <TableCell className="text-xs text-gray-600">{cp.employer || l.empregador || "—"}</TableCell>
                          <TableCell>
                            <Select value={l.tag_cliente || ""} onValueChange={(v) => mudarTag(l, v)}>
                              <SelectTrigger className={`h-7 w-[150px] text-xs border-0 ${l.tag_cliente ? TAG_META[l.tag_cliente] : "bg-slate-50 text-slate-400"}`} data-testid={`tag-select-${l.id}`}>
                                <SelectValue placeholder="Definir tag" />
                              </SelectTrigger>
                              <SelectContent>
                                {TAG_OPTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px] uppercase">{l.source || "—"}</Badge></TableCell>
                          <TableCell>
                            <Select value={l.status} onValueChange={(v) => mudarStatus(l, v)}>
                              <SelectTrigger className={`h-7 w-[150px] text-xs ${meta.cor} border-0`} data-testid={`status-select-${l.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.keys(STATUS_META).map((st) => <SelectItem key={st} value={st}>{STATUS_META[st].label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-xs">
                            {fmtDT(l.nova_vida_consultado_em) || <span className="text-amber-600">Sem consulta</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-violet-700 border-violet-200 hover:bg-violet-50"
                                onClick={() => setSimLead(l)} data-testid={`btn-consultar-${l.id}`} disabled={!l.dados_completos}
                                title={!l.dados_completos ? `Faltam dados para simular: ${(l.falta || []).join(", ")}` : "Consultar simulação"}>
                                <Search className="h-3.5 w-3.5" /> Consultar
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                onClick={() => setDocConsulta({ cpf: l.cpf, nome: l.nome, tipo: "fgts" })} data-testid={`btn-fgts-${l.id}`} disabled={!l.cpf}
                                title="Consultar saque FGTS via Docorban">
                                <Landmark className="h-3.5 w-3.5" /> FGTS
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-50"
                                onClick={() => setDocConsulta({ cpf: l.cpf, nome: l.nome, tipo: "clt" })} data-testid={`btn-clt-docorban-${l.id}`} disabled={!l.cpf}
                                title="Consultar CLT-Celcoin via Docorban">
                                <Briefcase className="h-3.5 w-3.5" /> CLT
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-blue-700 border-blue-200 hover:bg-blue-50"
                                onClick={() => setTimelineLead(l)} data-testid={`btn-timeline-${l.id}`} title="Linha do Tempo">
                                <Clock className="h-3.5 w-3.5" /> Linha do Tempo
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-green-600 border-green-200 hover:bg-green-50"
                                onClick={() => notificarWpp(l)} data-testid={`btn-wpp-${l.id}`} disabled={!l.pronto_contato}
                                title={!l.pronto_contato ? "Precisa de nome e telefone" : "Enviar WhatsApp"}>
                                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => excluir(l)} data-testid={`btn-del-${l.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </>
              )}
            </CardContent>
          </Card>

          {total > limit && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">{total} leads</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <span>{page} / {pages}</span>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)}>Próxima</Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ===== CONFIG ===== */}
        <TabsContent value="config" className="space-y-5">
          {config && (
            <>
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Webhook className="h-4 w-4" /> Webhook de recebimento</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-xs">URL do webhook (informe à Prospecta)</Label>
                    <div className="flex gap-2 mt-1">
                      <Input readOnly value={config.webhook_url || ""} className="font-mono text-xs" data-testid="webhook-url" />
                      <Button variant="outline" size="icon" onClick={() => copiar(config.webhook_url)}><Copy className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Chave secreta (HMAC-SHA256)</Label>
                    <Input value={config.webhook_secret || ""} onChange={(e) => setConfig({ ...config, webhook_secret: e.target.value })}
                      placeholder="Cole aqui a chave secreta combinada com a Prospecta" className="font-mono text-xs mt-1" data-testid="webhook-secret" />
                    <p className="text-[11px] text-gray-400 mt-1">Se preenchida, toda requisição é validada pela assinatura X-CreditCore-Sig. Deixe vazio só para testes.</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageCircle className="h-4 w-4" /> Notificação WhatsApp</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm">Notificar automaticamente ao receber lead novo</Label>
                      <p className="text-[11px] text-gray-400">Envia a mensagem abaixo para o telefone do lead assim que ele chega.</p>
                    </div>
                    <Switch checked={!!config.wpp_notificar} onCheckedChange={(v) => setConfig({ ...config, wpp_notificar: v })} data-testid="wpp-auto-switch" />
                  </div>
                  <div>
                    <Label className="text-xs">Chip WhatsApp p/ notificações (NUNCA a Central — evita banimento)</Label>
                    <Select value={config.wpp_session_id || "pabx_clt"} onValueChange={(v) => setConfig({ ...config, wpp_session_id: v })}>
                      <SelectTrigger className="mt-1" data-testid="wpp-session"><SelectValue placeholder="Selecione o chip" /></SelectTrigger>
                      <SelectContent>
                        {["pabx_clt","pabx_clt_01","pabx_clt_02","pabx_clt_03","pabx_clt_04","pabx_clt_05","pabx_clt_06","pabx_clt_07","pabx_inss","pabx_federal","pabx_municipal","pabx_estadual","pabx_exercito","pabx_aeronautica","pabx_marinha"].map((sid) => (
                          <SelectItem key={sid} value={sid}>{sid}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-amber-600 mt-1">⚠️ Conecte o chip escolhido em Atendimento WhatsApp (leitura do QR). A Central (nº principal) não é usada para disparos.</p>
                  </div>
                  <div>
                    <Label className="text-xs">Mensagem (variáveis: {"{primeiro_nome}"}, {"{nome}"}, {"{margem}"}, {"{empregador}"})</Label>
                    <Textarea rows={3} value={config.wpp_template || ""} onChange={(e) => setConfig({ ...config, wpp_template: e.target.value })} className="mt-1" data-testid="wpp-template" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /> Automação Prospecta CLT (UY3)</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between rounded-md border border-violet-200 bg-violet-50 px-3 py-2">
                    <div>
                      <Label className="text-sm text-violet-900">Direcionar leads automaticamente para a campanha VoIP</Label>
                      <p className="text-[11px] text-violet-700">Lead novo chega → consulta Nova Vida → entra na campanha "Prospecta CLT" (ligação → tecla 1 → SMS + WhatsApp) e a discagem inicia sozinha.</p>
                    </div>
                    <Switch checked={!!config.clt_auto_campanha} onCheckedChange={(v) => setConfig({ ...config, clt_auto_campanha: v })} data-testid="clt-auto-campanha-switch" />
                  </div>
                  <div>
                    <Label className="text-xs">ID da campanha VoIP de destino</Label>
                    <Input value={config.clt_campanha_voip_id || ""} onChange={(e) => setConfig({ ...config, clt_campanha_voip_id: e.target.value })} className="mt-1 font-mono text-xs" data-testid="clt-campanha-voip-id" />
                    <p className="text-[11px] text-gray-400 mt-1">Campanha da Central VoIP que fará a ligação. Configure o áudio/DTMF/SMS/WhatsApp diretamente na Central VoIP.</p>
                  </div>

                  <div className="border-t pt-4">
                    <Label className="text-xs font-semibold text-gray-500 uppercase">Notificação WhatsApp direto (uso manual / botão "Consultar")</Label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm">Simular automaticamente ao receber lead</Label>
                      <p className="text-[11px] text-gray-400">Roda a simulação UY3 assim que um lead CLT novo chega.</p>
                    </div>
                    <Switch checked={!!config.clt_auto_simular} onCheckedChange={(v) => setConfig({ ...config, clt_auto_simular: v })} data-testid="clt-auto-simular-switch" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm">Notificar automaticamente quando houver oferta</Label>
                      <p className="text-[11px] text-gray-400">Envia o WhatsApp com o valor pré-aprovado automaticamente.</p>
                    </div>
                    <Switch checked={!!config.clt_auto_notificar} onCheckedChange={(v) => setConfig({ ...config, clt_auto_notificar: v })} data-testid="clt-auto-notificar-switch" />
                  </div>
                  <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                    <div>
                      <Label className="text-sm text-amber-800">Modo de testes</Label>
                      <p className="text-[11px] text-amber-700">Quando LIGADO, todas as notificações vão para o número de teste abaixo (não dispara para clientes reais).</p>
                    </div>
                    <Switch checked={!!config.clt_test_mode} onCheckedChange={(v) => setConfig({ ...config, clt_test_mode: v })} data-testid="clt-test-mode-switch" />
                  </div>
                  <div>
                    <Label className="text-xs">Número de teste (recebe as mensagens em modo testes)</Label>
                    <Input value={config.clt_test_phone || ""} onChange={(e) => setConfig({ ...config, clt_test_phone: e.target.value })} placeholder="5591985690867" className="mt-1 font-mono text-sm" data-testid="clt-test-phone" />
                  </div>
                  <div>
                    <Label className="text-xs">Mensagem de oferta (variáveis: {"{primeiro_nome}"}, {"{nome}"}, {"{valor_liberado}"}, {"{parcelas}"}, {"{valor_parcela}"}, {"{banco}"})</Label>
                    <Textarea rows={6} value={config.clt_notif_template || ""} onChange={(e) => setConfig({ ...config, clt_notif_template: e.target.value })} className="mt-1 text-xs" data-testid="clt-notif-template" />
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <Label className="text-sm font-semibold">Disparo em massa → Campanha VoIP</Label>
                        <p className="text-[11px] text-gray-500">
                          Direciona <b>todos os leads CLT</b> para a campanha "Prospecta CLT": consulta Nova Vida (quem não tem) → adiciona o contato → inicia a discagem.
                          <span className="text-red-600"> ⚠️ Irá LIGAR para os clientes reais.</span>
                        </p>
                      </div>
                      <Button onClick={dispararTodos} disabled={bulk?.running} className="gap-2 bg-green-600 hover:bg-green-700" data-testid="btn-disparar-todos">
                        <MessageCircle className="h-4 w-4" /> {bulk?.running ? "Direcionando..." : "Notificar todos os clientes"}
                      </Button>
                    </div>
                    {bulk && (bulk.running || bulk.processados > 0) && (
                      <div className="mt-3 rounded-md border bg-slate-50 p-3 text-xs" data-testid="bulk-progress">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{bulk.running ? "Em andamento..." : (bulk.campanha_iniciada ? "Concluído — campanha iniciada ✅" : "Concluído")}</span>
                          <span>{bulk.processados || 0} / {bulk.total || 0}</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-full bg-green-500 transition-all" style={{ width: `${bulk.total ? Math.round((bulk.processados / bulk.total) * 100) : 0}%` }} />
                        </div>
                        <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-gray-600">
                          <span>✅ Adicionados: <b>{bulk.adicionados || 0}</b></span>
                          <span>🔎 Nova Vida: <b>{bulk.enriquecidos || 0}</b></span>
                          <span>♻️ Já na campanha: <b>{bulk.duplicados || 0}</b></span>
                          <span>📵 Sem telefone: <b>{bulk.sem_telefone || 0}</b></span>
                          <span>🚫 Blacklist: <b>{bulk.blacklist || 0}</b></span>
                          <span>⚠️ Erros: <b>{bulk.erros || 0}</b></span>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Credenciais API do Parceiro (fase 2 — saída)</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-xs text-gray-500">Usadas para simulação/digitação de propostas (a implementar). Tenant: <span className="font-mono">{config.tenant_id}</span></p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">E-mail</Label>
                      <Input value={config.api_email || ""} onChange={(e) => setConfig({ ...config, api_email: e.target.value })} className="mt-1" data-testid="api-email" />
                    </div>
                    <div>
                      <Label className="text-xs">Senha</Label>
                      <Input type="password" value={config.api_senha || ""} onChange={(e) => setConfig({ ...config, api_senha: e.target.value })} className="mt-1" data-testid="api-senha" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button onClick={salvarConfig} className="gap-2" data-testid="btn-salvar-config"><Save className="h-4 w-4" /> Salvar configuração</Button>
            </>
          )}
        </TabsContent>
      </Tabs>

      <ProspectaSimulacaoModal
        open={!!simLead}
        onClose={() => setSimLead(null)}
        cpf={simLead?.cpf}
        nome={simLead?.nome}
        telefone={simLead?.telefone}
        cliente={simLead}
      />
      <ProspectaTimelineModal
        open={!!timelineLead}
        onClose={() => setTimelineLead(null)}
        lead={timelineLead}
      />
      <DocorbanConsultaModal
        open={!!docConsulta}
        onClose={() => setDocConsulta(null)}
        cpf={docConsulta?.cpf}
        nome={docConsulta?.nome}
        tipo={docConsulta?.tipo}
      />
    </div>
  );
}
